import { MetaForm, MetaFormDisplayComponent, MetaFormService, MetaFormTextType, MetaFormUserEvent, MFValidator, UserEventType } from "@aifs-metaform/public-api";
import { FeatureService, FeatureType } from "@aifs-shared/feature/feature.service";

import { Component, computed, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { CfAlertComponent, CFAlertType } from "@ui/cf-alert/cf-alert.component";
import { CfButtonComponent, CFButtonStyle, CFButtonType } from "@ui/cf-button/cf-button.component";
import { Subscription } from "rxjs";
import { SystemAlertComponent } from "./system-alert.component";
import { UserService } from "@aifs-shared/user/user.service";

@Component({
    templateUrl: './sign-in.component.html',
    styleUrls: ['./sign-in.component.scss'],
    imports: [SystemAlertComponent, MetaFormDisplayComponent, CfButtonComponent, CfAlertComponent]
})

export class SignInComponent {
    ButtonStyle = CFButtonStyle;
    ButtonType = CFButtonType;
    AlertType = CFAlertType;

    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private metaformService = inject(MetaFormService);
    private features = inject(FeatureService);
    private userService = inject(UserService);

    isSignedIn = computed(() => { return false; })

    constructor() {
        if (this.isSignedIn()) {
            this.router.navigateByUrl('/home');
        }
        this.createSignInForm();
    }

    ngOnInit() {
        if (!this.features.areFeaturesLoaded()) {
            this.features.getFeatures()
                .subscribe(
                    r => {
                        this.checkFeatures();
                    }
                );
        } else {
            this.checkFeatures();
        }

        this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home'

        if (this.returnUrl!.startsWith('http') || this.returnUrl!.startsWith('HTTP')) {
            // console.warn(`Open redirect? ${this.returnUrl}`);
            this.returnUrl = '/home';
        }
    }

    ngOnDestroy() {
        this.formSubscription?.unsubscribe();
    }

    doSignIn() {
        if (this.isValid && this.form) {
            const e = this.form.getValue('email');
            const p = this.form.getValue('password');
            this.signInUser(e, p)
        }
    }

    doSignUp() {
        this.router.navigateByUrl("/register");
    }

    doReminder() {
        this.router.navigateByUrl('/forgot-password');
    }

    onFormEvent(event: MetaFormUserEvent): void {
        switch (event.type) {
            case UserEventType.FormInitialised:
            case UserEventType.FormInvalid:
            case UserEventType.FormInvalidOnAttemptedSubmit:
                this.isValid = false;
                break;
            case UserEventType.FormValid:
                // console.log(`The form is now valid`);
                this.isValid = true;
                break;
            case UserEventType.FormSubmit:
                if (this.isValid) this.doSignIn();
                break;
        }
    }

    private createSignInForm() {
        const f = this.metaformService.createForm('Sign-In', 'Sign In');

        f.addSection('Default');
        f.addQuestion('q_email')
            .addTextControl('email', MetaFormTextType.Email, 255, 'Email')
            .addValidator(MFValidator.Required('Please enter your email'))
            .addValidator(MFValidator.Email('Please enter your email'));

        f.addQuestion('q_password')
            .addTextControl('password', MetaFormTextType.Password, 255, 'Password')
            .addValidator(MFValidator.Required('Please enter your password'));

        this.form = f;
    }

    private checkFeatures() {
        this.closedForMaintenance = this.features.isFeatureEnabled(FeatureType.ClosedForMaintenance);
        this.signInPermitted = this.features.isFeatureEnabled(FeatureType.SignIn);
        this.signUpPermitted = this.features.isFeatureEnabled(FeatureType.Registration);

        // console.log(`Closed: ${this.closedForMaintenance}, Sign-In: ${this.signInPermitted}, Sign-Up: ${this.signUpPermitted}`);

        if (this.closedForMaintenance || !this.signInPermitted) {
            this.featuresDisabled = true;
            this.featureMessage = "The system is currently down for routine maintenance. Please check back again soon";
        } else {
            if (!this.signUpPermitted) {
                this.featuresDisabled = true;
                this.featureMessage = "New registrations are temporarily disabled. Please check back again later.";
            }
        }
    }

    private signInUser(email: string, password: string) {
        this.isProcessing = true;
        this.userService
            .login(email, password)
            .subscribe({
                next: (success: boolean) => {
                    if (this.userService.mustChangePassword()) {
                        // console.log(`User must change their password as it is in the old format`);
                        if (this.returnUrl) {
                            sessionStorage.setItem('returnUrl', this.returnUrl);
                        }
                        this.router.navigateByUrl(`/sign-in/reset-password/${this.userService.userId()}`);
                        return;
                    }

                    if (this.returnUrl) {
                        // console.debug(`applicant, navigate to ${this.returnUrl}`);
                        this.router.navigate([this.returnUrl]);
                    } else {
                        this.router.navigateByUrl('/home');
                    }
                },
                error: (error: any) => {
                    // something unexpected occured
                    this.errorOnLogin = true;
                    this.isProcessing = false;
                }
            });
    }

    returnUrl?: string;

    form?: MetaForm;
    isValid = false;

    signInPermitted = true;
    signUpPermitted = true;
    closedForMaintenance = false;
    featuresDisabled = false;
    featureMessage?: string;

    isProcessing = false;
    errorOnLogin = false;

    private formSubscription?: Subscription;

}		